/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import Render from '../../render/Render';
import useClockDialStyles from './ClockDial.styles';
import ClockDialFace from './ClockDialFace';
import {
  ClockDialView,
  getClockOptions
} from './ClockDialUtils';
import useClockDialInteractions from './useClockDialInteractions';

export interface ClockDialProps {
  onClose: () => void;
  onSelectHour: (hour: number) => void;
  onSelectMinute: (minute: number) => void;
  selectedHour: number;
  selectedMinute: number;
  view: ClockDialView | null;
}

/**
 * Renders a custom clock dial used by the DLS digital time picker.
 *
 * Label clicks select visible hour or five-minute labels, while drag
 * gestures on the minute dial can select exact minute values between
 * labels.
 *
 * ```typescript
 *
 * import {ClockDial} from '@ion/dls/src/components/date/ClockDial';
 *
 * <ClockDial view={ClockDialView.Hours}
 *   selectedHour={9}
 *   selectedMinute={30}
 *   onSelectHour={onSelectHour}
 *   onSelectMinute={onSelectMinute}
 *   onClose={onClose} />
 *
 * ```
 */
export const ClockDial = ({
  onClose,
  onSelectHour,
  onSelectMinute,
  selectedHour,
  selectedMinute,
  view
}: ClockDialProps) => {
  const classes = useClockDialStyles();
  const options = useMemo(() => getClockOptions(view), [view]);
  const {
    faceRef,
    onFaceMouseDown,
    onOptionClick,
    onOptionMouseDown,
    onRootMouseDown
  } = useClockDialInteractions({
    onClose,
    onSelectHour,
    onSelectMinute,
    options,
    view
  });

  return (
    <Render.If condition={!!view}>
      <div className={classes.root}
        onMouseDown={onRootMouseDown}>
        <div className={classes.container}>
          <ClockDialFace ref={faceRef}
            classes={classes}
            onFaceMouseDown={onFaceMouseDown}
            onOptionClick={onOptionClick}
            onOptionMouseDown={onOptionMouseDown}
            options={options}
            selectedHour={selectedHour}
            selectedMinute={selectedMinute}
            view={view} />
        </div>
      </div>
    </Render.If>
  );
};

export default ClockDial;
